import React, { Children, useEffect, useContext } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import PropTypes from 'prop-types';
import {
  createBrowserRouter,
  createRoutesFromElements,
  matchRoutes,
  Outlet,
  Route,
  RouterProvider,
  StaticRouterProvider
} from 'react-router';

import { useRouter } from 'app/helpers/routing';
import Context from '@bibliocommons/context';

import { RouteErrorElement } from 'app/components/errors/ErrorBoundary';
import { removeSessionStorageItem } from 'app/helpers/storage';
import { SEARCH_ORIGIN_QUERY_PARAM } from 'app/constants/AnalyticsConstants';
import { trackPageView } from 'app/actions/AnalyticsGa4Actions';
import { mapListToAnalyticsListEntity } from 'app/helpers/list';
import routes from './routes';

export function Routes() {
  const { location, query } = useRouter();
  const { backToLinkEnabled, hydrated } = useContext(Context);
  const dispatch = useDispatch();
  const globalList = useSelector(state => state.getIn(['list', 'list']));

  useEffect(() => {
    // Skip analytics tracking if we know that
    // the page component will immediately perform a router.replace
    let isSkipTracking = location.pathname === '/v2' || location.pathname === '/v2/';
    isSkipTracking =
      isSkipTracking || (location.pathname === '/v2/search' && Boolean(query[SEARCH_ORIGIN_QUERY_PARAM]));
    isSkipTracking = isSkipTracking || /^\/v2\/browse\/(genre|format|collection)/.test(location.pathname);

    let bibId = null;
    let list = null;
    let eventId = null;

    if (location.pathname.startsWith('/v2/record/')) {
      bibId = /^\/v2\/record\/([^/]+)\/?/.exec(location.pathname)?.[1] ?? null;
    } else if (location.pathname.startsWith('/v2/list/display')) {
      list = mapListToAnalyticsListEntity(globalList?.toJS());
    } else if (/^\/v2\/events\/[^/]+\/?$/.test(location.pathname)) {
      // matches /v2/events/{eventId}
      eventId = /^\/v2\/events\/([^/]+)\/?$/.exec(location.pathname)?.[1] ?? null;
    }

    if (!isSkipTracking) {
      dispatch(trackPageView({ bibId, list, eventId }));
    }

    // Update the active header nav everytime the location changes
    if (window.BCHeaderPublic) {
      window.BCHeaderPublic('setActiveNavItem');
    }
  }, [dispatch, globalList, location, query]);

  useEffect(() => {
    if (!backToLinkEnabled && hydrated) {
      removeSessionStorageItem('backToLink');
    }
  }, [backToLinkEnabled, hydrated]);

  // Both the client's data router and the server's static router (CORE-79041) render the route
  // tree through a router provider, so this is always a layout route and its match arrives through
  // the outlet.
  return <Outlet />;
}

/**
 * Collects every `createLoader` within a route's element, outermost first.
 *
 * The same walk fetchDataForCurrentRoutes does for `getRouteData`, and for the same reason: the
 * route tree nests the page component inside guards -- <AuthenticatedRoute><AccountRequiredRoute>
 * <CheckedOutPrintListRoute/></...></...> -- so the component that owns the data is usually not the
 * route's outermost element. Searching only the top would silently find nothing and render an
 * empty page.
 */
function collectLoaderFactories(element) {
  if (!element || !element.type) {
    return [];
  }

  const own = element.type.createLoader ? [element.type.createLoader] : [];
  const nested = Children.toArray(element.props?.children).flatMap(collectLoaderFactories);

  return [...own, ...nested];
}

/**
 * Hangs a `loader` on every route whose element chain declares one, bound to this store.
 *
 * A chain runs SEQUENTIALLY, which is what fetchDataForCurrentRoutes' `when/sequence` does today --
 * so a nested pair that depends on running in order (UsersPageRoute fetches the user that
 * ShelfPrintListRoute reads) keeps working while the migration is in flight. react-router runs
 * loaders of *different* routes in parallel, which is why that pair still has to become a real
 * parent/child route pair (CORE-79114).
 *
 * The loader returns null on purpose: route data lives in Redux and components read it through
 * selectors, so nothing calls useLoaderData (CORE-79042).
 *
 * Exported so a spec can assert the sequencing on a tree it builds itself -- createAppRoutes only
 * ever binds the app's own routes.
 */
export function bindLoaders(routeObjects, store, parentId = '') {
  routeObjects.forEach((route, index) => {
    const id = parentId ? `${parentId}-${index}` : `${index}`;
    const factories = collectLoaderFactories(route.element);

    if (factories.length) {
      // react-router assigns route ids itself, but not until a router is built from the tree --
      // createRoutesFromElements leaves them undefined. getHydrationData has to key its entries by
      // id BEFORE that happens, so a loader route gets an explicit one here and react-router keeps
      // it. The `loader:` prefix cannot collide with the ids react-router generates for the rest of
      // the tree, which are digits and dashes only; duplicates would make it throw.
      /* eslint-disable no-param-reassign */
      route.id = route.id ?? `loader:${id}`;
      route.loader = args =>
        factories
          .reduce((previous, createLoader) => previous.then(() => createLoader(store)(args)), Promise.resolve())
          .then(() => null);
      /* eslint-enable no-param-reassign */
    }

    if (route.children) {
      bindLoaders(route.children, store, id);
    }
  });

  return routeObjects;
}

// createBrowserRouter must be called once, and only in the browser. Lazily, so importing this
// module in a non-browser context (the server bundle, a test) creates nothing.
// Exported so a spec can assert the root route's errorElement on the tree the app actually builds:
// a test that constructs its own tree passes whether or not this one declares it.
//
// `store` is optional: without it the tree is built without loaders, which is all getCurrentRoutes
// and matchedRoutesContain need -- they build it purely to match a pathname against.
export function createAppRoutes(store) {
  const routeObjects = createRoutesFromElements(
    <Route element={<Routes />} errorElement={<RouteErrorElement />}>
      {routes.props.children}
    </Route>
  );

  return store ? bindLoaders(routeObjects, store) : routeObjects;
}

/**
 * Tells react-router that the initially-matched routes already have their data.
 *
 * The server ran their loaders and Iso serialised the resulting Redux state into the page, which
 * app/main.js rehydrated before this runs. Without this, createBrowserRouter runs every matched
 * loader again on mount -- refetching on every single page load exactly what SSR just fetched.
 * RouteWrapper avoided the same thing with its `applicationContext.hydrated` guard.
 *
 * `null` is the literally correct value here rather than a placeholder: loaders return null,
 * because the data they fetched lives in Redux and not in loaderData.
 *
 * Exported for a spec: the ids it emits have to be the same ones the router ends up using, and the
 * only way to know that holds is to build a router with this and watch the loader not run.
 */
export function getHydrationData(routeObjects, location = window.location) {
  const loaderData = {};

  (matchRoutes(routeObjects, location) ?? [])
    .filter(match => match.route.loader)
    .forEach(match => {
      loaderData[match.route.id] = null;
    });

  return { loaderData };
}

let dataRouter = null;
function getDataRouter(store) {
  if (!dataRouter) {
    const routeObjects = createAppRoutes(store);
    dataRouter = createBrowserRouter(routeObjects, {
      hydrationData: getHydrationData(routeObjects)
    });
  }

  return dataRouter;
}

function ClientRouter() {
  // The store comes from react-redux's Provider, which app/main.js already wraps this in. Not a
  // module-level singleton: that would be fine in a browser, where there is exactly one store per
  // page, but wrong on the server, where the store is per-request and a singleton would leak state
  // between concurrent requests.
  const store = useStore();

  return <RouterProvider router={getDataRouter(store)} />;
}

export default function ApplicationRouter({ router, context }) {
  // Both sides now render through a data router (CORE-79041): the client owns navigation itself,
  // which is what makes useBlocker available for ConfirmUnsavedChangesModal (CORE-78999); the
  // server has no history to own, but renders the same route tree the same way, via a router and
  // context built once per request by renderReact from react-router's static handler.
  if (__SERVER__) {
    return <StaticRouterProvider router={router} context={context} />;
  }

  return <ClientRouter />;
}

ApplicationRouter.propTypes = {
  router: PropTypes.object,
  context: PropTypes.object
};
